import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import {
  Ticket,
  Plus,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Clock,
  CheckCircle,
  ChevronRight,
  Paperclip,
  X,
  FileText,
  Image,
  File,
  Clipboard,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

const SupportTab = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isInsideSettings = location.pathname.startsWith('/settings');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // New ticket form
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const ticketFileInputRef = useRef(null);
  const descriptionRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const res = await api.get('/support/tickets');
        setTickets(res.data);
      } catch (err) {
        console.error('Failed to fetch tickets:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, [api]);

  const refetchTickets = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await api.get('/support/tickets');
      setTickets(res.data);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Navigate to ticket detail page
  const openTicketDetail = (ticketId) => {
    navigate(`/support/tickets/${ticketId}`, isInsideSettings ? { state: { from: 'settings' } } : undefined);
  };

  // Handle paste for images in description
  const handleDescriptionPaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        if (file.size > 5 * 1024 * 1024) {
          alert('Pasted image is too large. Maximum size is 5MB.');
          continue;
        }

        setUploading(true);
        try {
          const reader = new FileReader();
          const dataUrl = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
          setTicketAttachments(prev => [...prev, {
            filename: `pasted-image-${timestamp}.png`,
            url: dataUrl,
            content_type: file.type,
            size: file.size,
            isPasted: true
          }]);
        } catch (err) {
          console.error('Failed to read pasted image:', err);
        } finally {
          setUploading(false);
        }
      }
    }
  }, []);

  // File handling utilities
  const handleFileSelect = async (e, setAttachments, fileInputRef) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newAttachments = [];
    
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }
      
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newAttachments.push({
        filename: file.name,
        url: dataUrl,
        content_type: file.type,
        size: file.size
      });
    }
    
    setAttachments(prev => [...prev, ...newAttachments]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index, setAttachments) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.includes('pdf') || contentType?.includes('document')) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmitTicket = async () => {
    if (!subject.trim() || !description.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/support/tickets', {
        subject, 
        description, 
        category,
        attachments: ticketAttachments.length > 0 ? ticketAttachments : null
      });
      setSubject('');
      setDescription('');
      setCategory('general');
      setTicketAttachments([]);
      setShowNewTicket(false);
      refetchTickets();
    } catch (err) {
      console.error('Failed to create ticket:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-purple-500/20 text-purple-500 dark:text-purple-400 hover:bg-purple-500/30 border-transparent';
      case 'open': return 'bg-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-500/30 border-transparent';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-500/30 border-transparent';
      case 'waiting_on_customer': return 'bg-orange-500/20 text-orange-600 dark:text-orange-400 hover:bg-orange-500/30 border-transparent';
      case 'waiting_on_support': return 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/30 border-transparent';
      case 'resolved': return 'bg-green-500/20 text-green-600 dark:text-green-400 hover:bg-green-500/30 border-transparent';
      case 'closed': return 'bg-gray-500/20 text-gray-600 dark:text-gray-400 hover:bg-gray-500/30 border-transparent';
      case 'reopened_by_customer': return 'bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-500/30 border-transparent';
      default: return 'bg-gray-500/20 text-gray-600 dark:text-gray-400 hover:bg-gray-500/30 border-transparent';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
      case 'closed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'waiting_on_customer':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'waiting_on_support':
        return <Clock className="h-5 w-5 text-cyan-500" />;
      default:
        return <Clock className="h-5 w-5 text-blue-500" />;
    }
  };

  const openCount = tickets.filter(t => !['resolved', 'closed'].includes(t.status)).length;

  // Attachment preview component with inline image support for pasted images
  const AttachmentPreview = ({ attachments, setAttachments }) => (
    attachments.length > 0 && (
      <div className="space-y-2 mt-3">
        {attachments.map((file, index) => {
          const FileIcon = getFileIcon(file.content_type);
          const isImage = file.content_type?.startsWith('image/');
          
          // Show pasted images inline
          if (isImage && file.isPasted) {
            return (
              <div key={`attach-${index}`} className="relative inline-block">
                <img 
                  src={file.url} 
                  alt={file.filename}
                  className="max-w-xs rounded-lg border"
                />
                <button
                  onClick={() => removeAttachment(index, setAttachments)}
                  className="absolute -top-2 -right-2 p-1 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  <X className="h-3 w-3" />
                </button>
                <span className="absolute bottom-2 left-2 text-xs bg-black/50 text-white px-2 py-1 rounded">
                  Pasted image
                </span>
              </div>
            );
          }
          
          return (
            <div 
              key={`comment-${index}`}
              className="flex items-center gap-3 p-2 rounded-lg bg-secondary"
            >
              <FileIcon className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{file.filename}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
              </div>
              <button
                onClick={() => removeAttachment(index, setAttachments)}
                className="p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    )
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Support Tickets</h2>
          <p className="text-sm text-muted-foreground">
            {openCount} open ticket{openCount !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={refetchTickets}
            disabled={refreshing}
            data-testid="tickets-refresh-btn"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowNewTicket(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Ticket
          </Button>
        </div>
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Ticket className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-medium mb-1">No Support Tickets</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Need help? Create a support ticket and our team will assist you.
            </p>
            <Button onClick={() => setShowNewTicket(true)}>
              <Plus className="mr-2 h-4 w-4" /> Create Ticket
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <Card
              key={ticket.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => openTicketDetail(ticket.id)}
              data-testid={`ticket-card-${ticket.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    {getStatusIcon(ticket.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-muted-foreground">
                        {ticket.ticket_number}
                      </span>
                      <Badge className={getStatusColor(ticket.status)}>
                        {ticket.status?.replace(/_/g, ' ')}
                      </Badge>
                      <Badge variant="outline" className="capitalize">
                        {ticket.priority}
                      </Badge>
                    </div>
                    <h3 className="font-medium truncate">{ticket.subject}</h3>
                    <p className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of your issue"
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="technical">Technical Issue</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="feature_request">Feature Request</SelectItem>
                  <SelectItem value="bug_report">Bug Report</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <div className="relative">
                <Textarea
                  ref={descriptionRef}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onPaste={handleDescriptionPaste}
                  placeholder="Describe your issue in detail... (Tip: You can paste screenshots!)"
                  rows={5}
                  className="pr-10"
                />
                <div className="absolute right-3 top-3">
                  <Clipboard className="h-4 w-4 text-muted-foreground" title="Paste images with Ctrl+V" />
                </div>
              </div>
            </div>

            {/* File Attachments for New Ticket */}
            <div className="space-y-2">
              <Label>Attachments</Label>
              <input
                type="file"
                ref={ticketFileInputRef}
                onChange={(e) => handleFileSelect(e, setTicketAttachments, ticketFileInputRef)}
                multiple
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => ticketFileInputRef.current?.click()}
                disabled={uploading}
                className="w-full"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Paperclip className="h-4 w-4 mr-2" />
                )}
                Attach Files
              </Button>
              <p className="text-xs text-muted-foreground">
                Max 5MB per file. Images, PDFs, and documents accepted. You can also paste images directly!
              </p>
              <AttachmentPreview 
                attachments={ticketAttachments} 
                setAttachments={setTicketAttachments} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewTicket(false)}>Cancel</Button>
            <Button
              onClick={handleSubmitTicket}
              disabled={!subject.trim() || !description.trim() || submitting}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Submit Ticket'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportTab;
